// TerrainAdvisorSettings \u2014 sheet that opens behind the gear icon on
// TerrainAdvisorBar. Lets the pilot pick a preset, set custom
// thresholds, the activation ground-speed gate, and how the corridor
// is displayed.
//
// All state lives in useTerrainAdvisorStore; this sheet is purely UI.

import { useState } from 'react';
import {
  useTerrainAdvisorStore,
  TERRAIN_PRESETS,
  type TerrainPresetId,
  type CorridorMode,
} from '../stores/terrainAdvisorStore';

interface Props {
  onClose: () => void;
}

const PRESET_ORDER: TerrainPresetId[] = [
  'normal',
  'low',
  'heli-normal',
  'heli-medium',
  'heli-low',
  'custom',
];

export default function TerrainAdvisorSettings({ onClose }: Props) {
  const presetId          = useTerrainAdvisorStore((s) => s.presetId);
  const redFt             = useTerrainAdvisorStore((s) => s.redFt);
  const yellowFt          = useTerrainAdvisorStore((s) => s.yellowFt);
  const activationSpeedKt = useTerrainAdvisorStore((s) => s.activationSpeedKt);
  const corridorMode      = useTerrainAdvisorStore((s) => s.corridorMode);
  const corridorWidthNm   = useTerrainAdvisorStore((s) => s.corridorWidthNm);

  const alertsEnabled       = useTerrainAdvisorStore((s) => s.alertsEnabled);
  const setAlertsEnabled    = useTerrainAdvisorStore((s) => s.setAlertsEnabled);

  const setPreset           = useTerrainAdvisorStore((s) => s.setPreset);
  const setCustomThresholds = useTerrainAdvisorStore((s) => s.setCustomThresholds);
  const setActivationSpeedKt= useTerrainAdvisorStore((s) => s.setActivationSpeedKt);
  const setCorridorMode     = useTerrainAdvisorStore((s) => s.setCorridorMode);
  const setCorridorWidthNm  = useTerrainAdvisorStore((s) => s.setCorridorWidthNm);

  // Local input state for the custom-threshold fields so the user
  // can type a partial number without it being clamped every keystroke.
  const [customRed,    setCustomRed]    = useState<string>(String(redFt));
  const [customYellow, setCustomYellow] = useState<string>(String(yellowFt));
  const commitCustom = () => {
    const r = parseInt(customRed,    10);
    const y = parseInt(customYellow, 10);
    if (Number.isFinite(r) && Number.isFinite(y)) {
      setCustomThresholds(r, y);
    } else {
      setCustomRed(String(redFt));
      setCustomYellow(String(yellowFt));
    }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div
        className="bg-cockpit-900 border border-cockpit-600 rounded-t-2xl sm:rounded-2xl w-full sm:w-[28rem] max-h-[85vh] overflow-y-auto shadow-bar"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-cockpit-700">
          <div className="text-sm font-semibold tracking-wide text-cockpit-100">
            Terrain Advisor
          </div>
          <button
            onClick={onClose}
            className="text-cockpit-400 hover:text-cockpit-100 text-sm"
          >
            Done
          </button>
        </div>

        {/* Master alert toggle */}
        <Section title="Terrain alerts">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1">
              <span className="text-[12px] text-cockpit-100">
                {alertsEnabled ? 'Alerts on' : 'Alerts off'}
              </span>
              <div className="text-[10px] text-cockpit-400 mt-0.5">
                {alertsEnabled
                  ? 'Terrain proximity warnings will fire when airborne.'
                  : 'All terrain alert toasts suppressed. The map overlay still shows.'}
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={alertsEnabled}
              onClick={() => setAlertsEnabled(!alertsEnabled)}
              className={[
                'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none',
                alertsEnabled ? 'bg-ember-500' : 'bg-cockpit-600',
              ].join(' ')}
            >
              <span
                className={[
                  'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                  alertsEnabled ? 'translate-x-5' : 'translate-x-0',
                ].join(' ')}
              />
            </button>
          </div>
        </Section>

        {/* Preset picker — dimmed when alerts disabled */}
        <div className={alertsEnabled ? '' : 'opacity-40 pointer-events-none'}>
          <div className="px-4 pb-1 pt-2 text-[10px] text-cockpit-500 italic">
            {!alertsEnabled && 'Toggle “Terrain alerts” on to configure thresholds.'}
          </div>
        <Section title="Hazard altitudes">
          <div className="grid grid-cols-2 gap-2">
            {PRESET_ORDER.map((id) => {
              const isActive = presetId === id;
              const label = id === 'custom' ? 'Custom' : TERRAIN_PRESETS[id].label;
              const sub =
                id === 'custom'
                  ? `${redFt}' / ${yellowFt}'`
                  : `${TERRAIN_PRESETS[id].redFt}' / ${TERRAIN_PRESETS[id].yellowFt}'`;
              return (
                <button
                  key={id}
                  onClick={() => setPreset(id)}
                  className={
                    'rounded-lg border px-3 py-2 text-left transition-colors ' +
                    (isActive
                      ? 'border-ember-500 bg-cockpit-800 text-cockpit-100'
                      : 'border-cockpit-700 bg-cockpit-800/60 text-cockpit-300 hover:bg-cockpit-800')
                  }
                >
                  <div className="text-[12px] font-medium">{label}</div>
                  <div className="text-[10px] font-mono text-cockpit-400 mt-0.5">{sub}</div>
                </button>
              );
            })}
          </div>

          {presetId === 'custom' && (
            <div className="grid grid-cols-2 gap-2 mt-3">
              <label className="text-[11px] text-cockpit-300">
                <span className="block mb-1">Red threshold (ft)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={5000}
                  value={customRed}
                  onChange={(e) => setCustomRed(e.target.value)}
                  onBlur={commitCustom}
                  className="w-full bg-cockpit-800 border border-cockpit-600 rounded px-2 py-1 font-mono text-cockpit-100"
                />
              </label>
              <label className="text-[11px] text-cockpit-300">
                <span className="block mb-1">Yellow threshold (ft)</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={10000}
                  value={customYellow}
                  onChange={(e) => setCustomYellow(e.target.value)}
                  onBlur={commitCustom}
                  className="w-full bg-cockpit-800 border border-cockpit-600 rounded px-2 py-1 font-mono text-cockpit-100"
                />
              </label>
            </div>
          )}
        </Section>

        {/* Activation speed */}
        <Section
          title="Activation ground speed"
          hint="Suppress hazard coloring below this speed (no useful TAWS while parked / hovering)."
        >
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={activationSpeedKt}
              onChange={(e) => setActivationSpeedKt(parseInt(e.currentTarget.value, 10))}
              className="flex-1 accent-ember-500"
              aria-label="Activation ground speed knots"
            />
            <span className="font-mono text-[12px] text-cockpit-100 min-w-[3.5rem] text-right">
              {activationSpeedKt} kt
            </span>
          </div>
          <div className="text-[10px] text-cockpit-500 mt-1">
            Reads from live GPS / avionics when available. With no live speed
            source, the advisor stays on regardless.
          </div>
        </Section>

        {/* Corridor mode */}
        <Section
          title="Corridor display"
          hint="How widely to paint hazard shading: limit to the route corridor, paint everywhere, or hide the TAWS overlay."
        >
          <div className="grid grid-cols-3 gap-2">
            {(['auto', 'show', 'hide'] as CorridorMode[]).map((m) => {
              const isActive = corridorMode === m;
              const label = m === 'auto' ? 'Corridor only' : m === 'show' ? 'Whole map' : 'Hide';
              return (
                <button
                  key={m}
                  onClick={() => setCorridorMode(m)}
                  className={
                    'rounded-lg border px-2 py-1.5 text-[11px] ' +
                    (isActive
                      ? 'border-ember-500 bg-cockpit-800 text-cockpit-100'
                      : 'border-cockpit-700 bg-cockpit-800/60 text-cockpit-300 hover:bg-cockpit-800')
                  }
                >
                  {label}
                </button>
              );
            })}
          </div>

          {corridorMode === 'auto' && (
            <div className="mt-3">
              <div className="text-[11px] text-cockpit-300 mb-1">
                Corridor width
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={50}
                  step={1}
                  value={corridorWidthNm}
                  onChange={(e) => setCorridorWidthNm(parseInt(e.currentTarget.value, 10))}
                  className="flex-1 accent-ember-500"
                  aria-label="Corridor half-width nautical miles"
                />
                <span className="font-mono text-[12px] text-cockpit-100 min-w-[4.5rem] text-right">
                  ±{corridorWidthNm} nm
                </span>
              </div>
              <div className="text-[10px] text-cockpit-500 mt-1">
                Half-width on each side of the active flight plan line. With no
                active route, behaves as 'Whole map'. — <em>spatial corridor
                clipping is a coming-soon refinement; today 'Corridor only'
                still paints the entire visible map.</em>
              </div>
            </div>
          )}
        </Section>
      </div>
        </div>{/* end dimming wrapper */}
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-4 py-3 border-b border-cockpit-800/80 last:border-b-0 space-y-2">
      <div>
        <div className="text-[12px] font-semibold tracking-wide text-cockpit-100">
          {title}
        </div>
        {hint && <div className="text-[10px] text-cockpit-500 mt-0.5">{hint}</div>}
      </div>
      {children}
    </div>
  );
}
